import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';

import { useAppContext } from '@prisjakt/ui-app-context';

import { ParamsAndReturnTypes } from '@prisjakt/ui-translate/src/translate-func';

import { IconSort } from '@prisjakt/ui/icon';

import { MediumDown } from 'src/ui/block';
import { pushEvent } from 'src/client/data-layer';
import OverlaySort from 'src/areas/product-collection/components/productOffersContainer/overlay-sort';
import Pagination from 'src/areas/product-collection/components/productOffersContainer/pagination';
import { getTranslationForSortOption } from 'src/areas/product-collection/components/productOffersContainer/sort-options';
import { CompactListLayout } from 'src/areas/product-collection/components/products/layout-compact-list';
import { GridLayout } from 'src/areas/product-collection/components/products/layout-grid';
import { ListLayout } from 'src/areas/product-collection/components/products/layout-list';
import {
  ToggleButton,
  VariantEnum,
} from 'src/areas/product-collection/components/toggle-button';
import { productPriceClick } from 'src/areas/product/actions/product';
import { Option } from 'src/components/filter-dropdown';
import { NoSearchResults } from 'src/components/no-search-results';
import { UxLiteQuestions } from 'src/components/ux-feedback';
import { ResultFilters } from '../../filters';
import { SearchResultsProductsHeader } from '../../panel-header';
import { SortedBy } from '../../sorted-by';

import { useActiveCampaignByProductId } from 'src/hooks/active-campaign-by-product-id';
import { useCoolDown } from 'src/hooks/use-cool-down';
import { useListCardClickoutExperiment } from 'src/hooks/experiments';
import { useEffectOnMount } from 'src/hooks/useEffectOnMount';
import { useLocalStorage } from 'src/hooks/use-localstorage';

import { NAV_HEIGHT } from 'src/components/sticky-tab-nav/constants';
import { MAX_RESULTS } from '../../../constants';

import {
  trackSearchResultProductClicked,
  trackSearchResultProductsListed,
  trackSearchResultRawOfferClicked,
  trackSearchResultSwitchLayout,
} from '../../../tracking';

import { LayoutMode } from 'src/components/layout-buttons/types';
import {
  Offer,
  Product,
  Raw,
  SearchFacetInterval,
  SearchFacetList,
  SearchPageInfo,
  SearchResultFilters,
  SortInfo,
} from 'src/types';
import { TabAnchor } from '../../navigation/types';
import { Filter, SearchFacetType } from '../types';

import * as S from '../styles';
import { SearchRelevancyQuestions } from 'src/components/ux-feedback/search-relevancy-question';
import {
  TrackClickScope,
  trackOfferClickWithRevenue,
} from 'src/globals/tracking/conversion';

type NewFacets = SearchResultFilters;

type OldFacets = (SearchFacetList | SearchFacetInterval | null)[] | null;

type Props = {
  visible: boolean;
  facets?: OldFacets | NewFacets;
  pageInfo?: SearchPageInfo | null;
  products: (
    | Omit<Product, 'classicFullPath' | 'conditions' | 'media'>
    | Omit<Raw, 'store'>
    | Omit<
        Offer,
        | 'stockStatus'
        | 'id'
        | 'store'
        | 'media'
        | 'name'
        | 'externalUri'
        | 'offerPrice'
      >
    | null
  )[];
  productsCount: number;
  sort?: SortInfo | null;
  query: string;
  newSearch: boolean;
  isWide?: boolean;
  embedPanoramaAd?: boolean;
};

const ADS_LAYOUT = {
  [LayoutMode.GRID]: {
    panorama_extra: { show: false, row: 2 },
    dubbelpanorama1: { show: true, row: 2 },
    dubbelpanorama2: { show: true, row: 8 },
  },
  [LayoutMode.LIST]: {
    panorama_extra: { show: false, row: 2 },
    dubbelpanorama1: { show: true, row: 3 },
    dubbelpanorama2: { show: true, row: 8 },
  },
  [LayoutMode.COMPACT_LIST]: {
    panorama_extra: { show: false, row: 2 },
    dubbelpanorama1: { show: true, row: 3 },
    dubbelpanorama2: { show: true, row: 15 },
  },
};

const ONE_DAY = 86400 * 1000;

const RND = Math.round(Math.random());

export const ProductResults = (props: Props) => {
  const {
    visible = true,
    facets,
    pageInfo,
    products,
    productsCount,
    sort,
    query,
    newSearch,
    isWide,
    embedPanoramaAd,
  } = props;

  const clickOutExperiment = useListCardClickoutExperiment();

  const { t } = useAppContext();
  const dispatch = useDispatch();
  const productsContainer = useRef<HTMLDivElement>(null);

  const [activeLayout, setLayoutState] = useState<LayoutMode>(
    LayoutMode.COMPACT_LIST
  );

  const [mobileSortOpen, setMobileSortOpen] = useState(false);

  const [searchUXFeedbackTime] = useLocalStorage('pj:searchUXFeedbackTime', 0);
  const [searchRelevancyFeedbackTime] = useLocalStorage(
    'pj:searchRelevancyFeedbackTime',
    0
  );

  const showUXFeedback = useCoolDown(ONE_DAY * 7, searchUXFeedbackTime);

  const showRelevancyFeedback = useCoolDown(
    ONE_DAY,
    searchRelevancyFeedbackTime
  );

  const [persistentLayout, setPersistentLayout] = useLocalStorage(
    'pj:searchResultsLayout',
    clickOutExperiment ? LayoutMode.LIST : LayoutMode.COMPACT_LIST
  );

  useEffectOnMount(() => {
    trackSearchResultProductsListed(activeLayout, query);
  });

  useEffect(() => {
    if (activeLayout) {
      dispatch({
        type: 'ADS_VIEW_UPDATE',
        view: `search:layout:${activeLayout}`,
      });
    }
  }, [activeLayout, dispatch]);

  useEffect(() => {
    // Change layout to the last used mode saved in localStorage
    if (persistentLayout) {
      setLayoutState(persistentLayout);
    }
  }, []);
  const availableSortTranslations: Record<string, string> = {
    'score:desc': 'relevance',
    'lowest_price:asc': 'price:asc',
    'lowest_price:desc': 'price:desc',
    'product_name:asc': 'name',
    'user_rating:desc': 'user_reviews',
  };

  const currentSort = `${sort?.name}:${sort?.direction}`;

  const currentTranslatedSort = getTranslationForSortOption(
    currentSort,
    availableSortTranslations,
    t
  );

  const productIds = useMemo(
    () =>
      products?.length
        ? (products
            .map(product => (product && 'id' in product ? product.id : null))
            .filter(id => id) as number[])
        : [],
    [products]
  );
  const productsWithActiveCampaign = useActiveCampaignByProductId(
    productIds,
    true
  );

  const setLayout = (layout: LayoutMode) => {
    if (activeLayout !== layout) {
      trackSearchResultSwitchLayout(activeLayout, layout);
    }

    setLayoutState(layout);
    setPersistentLayout(layout);
  };

  const handlePaginationClick = () => {
    if (!IS_BROWSER || !productsContainer.current?.offsetTop) {
      return;
    }

    window.scrollTo({
      top: productsContainer.current?.offsetTop - NAV_HEIGHT,
      behavior: 'smooth',
    });
  };

  const getFacetFilters = (): Filter[] | undefined => {
    const oldFacets = facets as OldFacets;
    const filters = oldFacets?.map(facet => {
      if (!facet) return null;

      const filterTitle: string = t(
        'search',
        `facets.title-${facet.id}` as keyof ParamsAndReturnTypes['search']
      );

      switch (facet.__typename) {
        case SearchFacetType.SEARCH_FACET_LIST:
          return {
            options: facet.buckets
              .map(item => {
                if (!item) return null;
                return {
                  ...item,
                  value: item?.name,
                };
              })
              .flatMap(item => (item ? [item] : [])),
            title: filterTitle,
            type: SearchFacetType.SEARCH_FACET_LIST,
            id: facet.id,
          };
        default:
          return null;
      }
    });

    return filters?.flatMap(item => (item ? [item] : []));
  };

  const onProductClickHandler = (
    id: string | number,
    name?: string,
    type?: string
  ) => {
    if (type === 'Raw' || type === 'Offer') {
      const clickedProduct = products.find(p => {
        const offerId = (p as Offer)?.id;
        const offerName = (p as Raw | Offer)?.name;

        return offerId === id || offerName === name;
      }) as Offer | Raw | undefined;

      trackSearchResultRawOfferClicked(
        query,
        clickedProduct?.store?.featured,
        name
      );

      // Only enable clickout and revenue tracking for featured stores
      if (clickedProduct?.store?.featured) {
        dispatch(productPriceClick(null, null, true, 'raw'));

        pushEvent('rawPriceClickRevenue', { name });

        trackOfferClickWithRevenue({
          scope: TrackClickScope.SEARCH_RESULT_PAGE,
          offerId: (clickedProduct as any)?._id,
        });
      }
    }

    if (type === 'Product') trackSearchResultProductClicked(id, query, name);
  };

  const toggleMobileSort = useCallback(() => {
    setMobileSortOpen(!mobileSortOpen);
  }, [mobileSortOpen]);

  const onClose = useCallback(() => {
    setMobileSortOpen(false);
  }, []);

  const newFacets = facets as NewFacets;
  const newFilters = [
    {
      id: 'category_id',
      type: 'SearchFacetList',
      title: t('search', 'facets.title-category_id'),
      options: newFacets?.category?.map(
        f =>
          f && {
            name: f.name,
            value: f.name,
            id: f.id,
            count: f.productCount,
          }
      ),
    },
    {
      id: 'brand_id',
      type: 'SearchFacetList',
      title: t('search', 'facets.title-brand_id'),
      options: newFacets?.brand?.map(
        f =>
          f && {
            name: f.name,
            value: f.name,
            id: f.id,
            count: f.productCount,
          }
      ),
    },
  ]
    .filter(Boolean)
    .filter(item => item.options && item.options.length > 0) as Filter[];

  const filters = newSearch ? newFilters : getFacetFilters();
  const totalProducts = pageInfo?.total || 0;

  const sortOptions: Option[] = [
    {
      title: t('common', 'sort.relevance'),
      value: `score:desc`,
    },
    {
      title: t('common', `sort.price:asc`),
      value: `lowest_price:asc`,
    },
    {
      title: t('common', `sort.price:desc`),
      value: `lowest_price:desc`,
    },
    {
      title: t('common', `sort.name`),
      value: `product_name:asc`,
    },
    {
      title: t('common', `sort.user_reviews`),
      value: `user_rating:desc`,
    },
  ];

  const hasFeedback = showRelevancyFeedback || showUXFeedback;
  const adsLayout = embedPanoramaAd
    ? {
        ...ADS_LAYOUT,
        [LayoutMode.GRID]: {
          ...ADS_LAYOUT[LayoutMode.GRID],
          panorama_extra: {
            show: true,
            row: hasFeedback ? 4 : 3,
          },
        },
        [LayoutMode.LIST]: {
          ...ADS_LAYOUT[LayoutMode.LIST],
          panorama_extra: {
            show: true,
            row: hasFeedback ? 6 : 5,
          },
        },
        [LayoutMode.COMPACT_LIST]: {
          ...ADS_LAYOUT[LayoutMode.COMPACT_LIST],
          panorama_extra: {
            show: true,
            row: hasFeedback ? 14 : 13,
          },
        },
      }
    : ADS_LAYOUT;

  const getFeedbackQuestion = () => {
    const uxLite = <UxLiteQuestions name="search" />;
    const relevancy = <SearchRelevancyQuestions />;

    if (!hasFeedback) {
      return null;
    }

    if (showRelevancyFeedback && showUXFeedback) {
      return RND === 0 ? relevancy : uxLite;
    }

    if (showRelevancyFeedback) {
      return relevancy;
    }

    return uxLite;
  };

  return (
    <div ref={productsContainer}>
      <S.Section
        id={TabAnchor.PRODUCTS}
        $visible={visible}
        $tab={TabAnchor.PRODUCTS}
      >
        <SearchResultsProductsHeader
          activeLayout={activeLayout}
          productsCount={productsCount}
          setLayout={setLayout}
          currentSort={currentSort}
          sortOptions={sortOptions}
          newSearch={newSearch}
        />

        <S.FilterWrapper>
          {filters && (
            <ResultFilters
              productsCount={productsCount}
              filters={filters}
              query={query}
            />
          )}

          {newSearch ? (
            <MediumDown>
              <SortedBy />
            </MediumDown>
          ) : (
            <MediumDown>
              <div>
                {mobileSortOpen && (
                  <OverlaySort
                    currentSort={currentSort}
                    onClose={onClose}
                    sortOptions={
                      sortOptions.map(option => option.value) as string[]
                    }
                    sortTranslations={availableSortTranslations}
                  />
                )}
                <ToggleButton
                  variant={VariantEnum.OUTLINED}
                  icon={<IconSort />}
                  onToggle={toggleMobileSort}
                  title={currentTranslatedSort}
                  truncate
                  data-test="SortOrderButton"
                />
              </div>
            </MediumDown>
          )}
        </S.FilterWrapper>

        {!products.length ? (
          <NoSearchResults />
        ) : (
          <>
            {activeLayout === LayoutMode.GRID && (
              <GridLayout
                feedbackPrompt={getFeedbackQuestion()}
                feedbackPromptPosition={6}
                productsWithActiveCampaign={productsWithActiveCampaign}
                products={
                  products.filter(
                    p =>
                      p?.__typename === 'Product' ||
                      p?.__typename === 'Raw' ||
                      p?.__typename === 'Offer'
                  ) as (Product | Raw)[]
                }
                adslayoutConfig={adsLayout}
                onClick={onProductClickHandler}
                isWide={isWide}
                embedPanoramaAd={embedPanoramaAd}
                hasFeedback={hasFeedback}
              />
            )}
            {activeLayout === LayoutMode.LIST && (
              <ListLayout
                feedbackPrompt={getFeedbackQuestion()}
                feedbackPromptPosition={24}
                productsWithActiveCampaign={productsWithActiveCampaign}
                products={
                  products.filter(
                    p =>
                      p?.__typename === 'Product' ||
                      p?.__typename === 'Raw' ||
                      p?.__typename === 'Offer'
                  ) as (Product | Raw)[]
                }
                adslayoutConfig={adsLayout}
                onClick={onProductClickHandler}
                embedPanoramaAd={embedPanoramaAd}
                hasFeedback={hasFeedback}
              />
            )}

            {activeLayout === LayoutMode.COMPACT_LIST && (
              <CompactListLayout
                feedbackPrompt={getFeedbackQuestion()}
                feedbackPromptPosition={24}
                productsWithActiveCampaign={productsWithActiveCampaign}
                products={
                  products.filter(
                    p =>
                      p?.__typename === 'Product' ||
                      p?.__typename === 'Raw' ||
                      p?.__typename === 'Offer'
                  ) as (Product | Raw)[]
                }
                adslayoutConfig={adsLayout}
                onClick={onProductClickHandler}
                embedPanoramaAd={embedPanoramaAd}
                hasFeedback={hasFeedback}
              />
            )}

            {pageInfo?.limit && productsCount > pageInfo?.limit && (
              <Pagination
                pageInfo={pageInfo}
                total={
                  totalProducts >= MAX_RESULTS ? MAX_RESULTS : totalProducts
                }
                onClick={handlePaginationClick}
              />
            )}
          </>
        )}
      </S.Section>
    </div>
  );
};
